import { useEffect, useState, useRef } from "react";
import { Download, Upload, Database, Fingerprint, Copy, AlertTriangle, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Migrazione anteprima → produzione.
 * Due azioni:
 *  - Esporta: scarica un file .enc + mostra la chiave UNA volta
 *  - Importa: carica file + chiave → sostituisce le collezioni
 * La UI mostra le "impronte" dei segreti d'ambiente (sha256[:8]) senza esporli.
 */
export default function MigrationPanel() {
  const [fp, setFp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exportResult, setExportResult] = useState(null);
  const [impFile, setImpFile] = useState(null);
  const [impKey, setImpKey] = useState("");
  const [impConfirm, setImpConfirm] = useState("");
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef(null);
  const impFileRef = useRef(null);

  const loadFp = async () => {
    try {
      const r = await api.get("/settings/migration/key-fingerprints");
      setFp(r.data);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { loadFp(); }, []);

  const doExport = async () => {
    setLoading(true); setExportResult(null);
    try {
      const r = await api.post("/settings/migration/export");
      const bin = Uint8Array.from(atob(r.data.ciphertext_b64), c => c.charCodeAt(0));
      const blob = new Blob([bin], { type: "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = r.data.filename; a.click();
      URL.revokeObjectURL(url);
      setExportResult({
        key: r.data.onetime_key_b64,
        filename: r.data.filename,
        counts: r.data.counts,
        env_fingerprints: r.data.env_fingerprints,
      });
      toast.success("Dump esportato. Copia subito la chiave (viene mostrata una sola volta).");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore export");
    } finally { setLoading(false); }
  };

  const doImport = async (e) => {
    e.preventDefault();
    if (!impFile) { toast.error("Seleziona il file .enc"); return; }
    if (!impKey.trim()) { toast.error("Incolla la chiave monouso"); return; }
    if (impConfirm.trim().toUpperCase() !== "SOSTITUISCI") {
      toast.error("Digita SOSTITUISCI per confermare"); return;
    }
    setLoading(true); setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", impFile);
      fd.append("onetime_key_b64", impKey.trim());
      fd.append("confirm", impConfirm);
      const r = await api.post("/settings/migration/import-ui", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(r.data);
      toast.success("Import completato. Backup disponibile con suffix " + r.data.backup_suffix);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore import");
    } finally { setLoading(false); }
  };

  const copyKey = (k) => {
    navigator.clipboard.writeText(k);
    toast.success("Chiave copiata nella clipboard");
  };

  return (
    <div className="glass rounded-2xl p-6" data-testid="migration-panel">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Database size={16} className="text-[#ff0033]" />
          <div className="font-display font-bold uppercase tracking-wider text-sm">Migrazione anteprima → produzione</div>
        </div>
        {fp && (
          <div className="flex items-center gap-3 text-[11px] font-mono text-white/60">
            <span className="flex items-center gap-1">
              <Fingerprint size={12} />
              PII: <span className="text-white/90">{fp.pii_encryption_key}</span>
            </span>
            <span>JWT: <span className="text-white/90">{fp.jwt_secret}</span></span>
          </div>
        )}
      </div>
      <p className="text-xs text-white/45 mb-4">
        Esporta tutte le collezioni applicative in un file cifrato AES-GCM 256 con chiave monouso. In produzione, importa file + chiave: fa backup e sostituisce. Confronta le impronte PII/JWT in questa pagina su entrambi gli ambienti — se differiscono, i CF e la chiave Stripe cifrati diventano illeggibili.
      </p>

      {/* --- Esporta --- */}
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-wider text-white/50">1. Esporta (anteprima)</div>
          <button data-testid="migration-export-btn" disabled={loading} onClick={doExport}
            className="btn-neon px-5 py-2 rounded-full text-xs inline-flex items-center gap-2 disabled:opacity-40">
            {loading ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
            Esporta dump cifrato
          </button>
        </div>
        {exportResult && (
          <div className="space-y-3" data-testid="migration-export-result">
            <div className="rounded-lg border border-amber-400/40 bg-amber-400/5 p-3">
              <div className="flex items-center gap-2 text-amber-300 text-xs mb-2">
                <AlertTriangle size={14} /> Chiave monouso — copiala SUBITO. Non verrà mostrata di nuovo.
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs bg-black/40 rounded px-2 py-1 font-mono break-all" data-testid="migration-onetime-key">
                  {exportResult.key}
                </code>
                <button onClick={() => copyKey(exportResult.key)}
                  className="px-3 py-1 rounded-full text-xs bg-white/[0.06] hover:bg-white/15 inline-flex items-center gap-1">
                  <Copy size={12} /> Copia
                </button>
              </div>
            </div>
            <div className="text-xs">
              <div className="text-white/50 uppercase tracking-wider mb-1">Collezioni esportate</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-1 text-white/70">
                {Object.entries(exportResult.counts).map(([k, v]) => (
                  <div key={k} className="font-mono">
                    {k}: <span className="text-white">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* --- Importa --- */}
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
        <div className="text-xs uppercase tracking-wider text-white/50 mb-3">2. Importa (produzione)</div>
        <form onSubmit={doImport} className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/40">File .enc</label>
            <input ref={impFileRef} data-testid="migration-import-file" type="file" accept=".enc,.bin,.bin.enc"
              onChange={(e) => setImpFile(e.target.files?.[0] || null)}
              className="mt-1 w-full text-xs text-white/70 file:mr-3 file:px-3 file:py-1.5 file:rounded-full file:border-0 file:bg-white/[0.06] file:text-white/90 file:text-xs hover:file:bg-white/10" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/40">Chiave monouso (base64)</label>
            <input data-testid="migration-import-key" type="password" autoComplete="off"
              value={impKey} onChange={(e) => setImpKey(e.target.value)}
              placeholder="incolla qui la chiave fornita dall'export"
              className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm font-mono focus:border-[#ff0033]/60 focus:outline-none" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/40">
              Conferma: digita <code className="text-[#ff0033]">SOSTITUISCI</code>
            </label>
            <input data-testid="migration-import-confirm" type="text"
              value={impConfirm} onChange={(e) => setImpConfirm(e.target.value)}
              className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm font-mono uppercase focus:border-[#ff0033]/60 focus:outline-none" />
          </div>
          <div className="flex justify-end">
            <button type="submit" disabled={loading} data-testid="migration-import-btn"
              className="btn-neon px-5 py-2 rounded-full text-xs inline-flex items-center gap-2 disabled:opacity-40">
              {loading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
              Importa (sostituisce dati esistenti)
            </button>
          </div>
        </form>

        {importResult && (
          <div className="mt-4 rounded-lg border border-emerald-400/40 bg-emerald-400/5 p-3" data-testid="migration-import-result">
            <div className="flex items-center gap-2 text-emerald-300 text-xs mb-2">
              <CheckCircle2 size={14} /> Import completato · backup suffix: <code className="font-mono">{importResult.backup_suffix}</code>
            </div>
            {importResult.env_fingerprints?.mismatch
              && Object.keys(importResult.env_fingerprints.mismatch).length > 0 && (
              <div className="rounded border border-amber-400/40 bg-amber-400/5 p-2 text-amber-300 text-xs mb-2">
                <AlertTriangle size={12} className="inline mr-1" />
                Impronte segreti divergenti: {Object.keys(importResult.env_fingerprints.mismatch).join(", ")} → i CF cifrati e la chiave Stripe potrebbero NON essere leggibili. Allinea i secrets prima di continuare.
              </div>
            )}
            <div className="text-xs text-white/70 grid grid-cols-2 md:grid-cols-3 gap-1">
              {Object.entries(importResult.collections_after || {}).map(([k, v]) => {
                const before = (importResult.collections_before || {})[k] ?? "?";
                return (
                  <div key={k} className="font-mono">
                    {k}: <span className="text-white/50">{before}</span>→<span className="text-white">{v}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
